import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Sidebar collapse state, ported from modern-accounting's chrome. Persists to the
 * `sidebar-state` localStorage key (debounced) so the collapsed preference sticks.
 */
const STORAGE_KEY = 'sidebar-state';

interface SidebarContextValue {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  /** Mobile (< md) off-canvas drawer open state. Not persisted — the drawer always starts closed. */
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  toggleMobileOpen: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

function readStored(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { isCollapsed?: boolean };
      return Boolean(parsed.isCollapsed);
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function SidebarProvider({ children }: { children: ReactNode }): ReactNode {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => readStored());

  useEffect(() => {
    const handle = window.setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ isCollapsed }));
      } catch {
        /* ignore */
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [isCollapsed]);

  const toggleCollapsed = useCallback(() => setIsCollapsed((c) => !c), []);
  const setCollapsed = useCallback((collapsed: boolean) => setIsCollapsed(collapsed), []);

  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleMobileOpen = useCallback(() => setMobileOpen((o) => !o), []);

  const value = useMemo<SidebarContextValue>(
    () => ({ isCollapsed, toggleCollapsed, setCollapsed, mobileOpen, setMobileOpen, toggleMobileOpen }),
    [isCollapsed, toggleCollapsed, setCollapsed, mobileOpen, toggleMobileOpen]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return ctx;
}
